import React, { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import COLORS from '../../../constants/colors';
import { PlanRulesChange } from '../../../types/planRules';

interface ReviewDialogProps {
  open: boolean;
  changes: PlanRulesChange[];
  /** Warnings not tied to one change (e.g. the free plan allows more than a paid plan). */
  extraWarnings: Array<{ text: string; highRisk?: boolean }>;
  publishing: boolean;
  onCancel: () => void;
  onPublish: (note: string) => void;
}

const CONFIRM_WORD = 'PUBLISH';

/** Before → after in plain words, automatic warnings, a "why" note. High-risk changes need PUBLISH typed. */
export function ReviewDialog({ open, changes, extraWarnings, publishing, onCancel, onPublish }: ReviewDialogProps) {
  const [note, setNote] = useState('');
  const [confirmWord, setConfirmWord] = useState('');

  useEffect(() => {
    if (open) {
      setNote('');
      setConfirmWord('');
    }
  }, [open]);

  const warnings = [...changes.flatMap((change) => (change.warning ? [change.warning] : [])), ...extraWarnings];
  const highRisk = warnings.some((warning) => warning.highRisk);
  const canPublish = !publishing && (!highRisk || confirmWord.trim() === CONFIRM_WORD);

  return (
    <Dialog open={open} onClose={publishing ? undefined : onCancel} maxWidth="md" fullWidth>
      <DialogTitle>
        Review {changes.length} change{changes.length === 1 ? '' : 's'}
        <Typography sx={{ color: COLORS.LIGHT_GRAY, fontSize: '0.875rem' }}>
          They go live for <strong>all users immediately</strong> after you publish.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <List dense sx={{ border: `1px solid ${COLORS.XX_LIGHT_GREEN}`, borderRadius: '10px', p: 0 }}>
          {changes.map((change) => (
            <ListItem
              key={change.key}
              divider
              sx={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}
            >
              <Typography sx={{ color: COLORS.DARK_GREY }}>{change.where}</Typography>
              <Typography>
                <Box component="span" sx={{ color: COLORS.LIGHT_GRAY, textDecoration: 'line-through' }}>
                  {change.from}
                </Box>
                {' → '}
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {change.to}
                </Box>
              </Typography>
            </ListItem>
          ))}
        </List>

        {warnings.map((warning) => (
          <Alert key={warning.text} severity={warning.highRisk ? 'error' : 'warning'} sx={{ mt: '8px' }}>
            {warning.text}
          </Alert>
        ))}

        <Typography sx={{ fontSize: '0.8125rem', color: COLORS.LIGHT_GRAY, my: '12px' }}>
          Users who already have more than a new limit keep what they have. Only new actions are checked. Selling is
          never blocked.
        </Typography>

        <TextField
          label="Why are you making this change? (saved in the history)"
          placeholder="e.g. Ramadan campaign: free users get 3 Saudi funds"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          fullWidth
          multiline
          minRows={2}
          inputProps={{ maxLength: 500 }}
        />

        {highRisk && (
          <TextField
            sx={{ mt: '16px' }}
            label={`High-risk change. Type ${CONFIRM_WORD} to confirm`}
            value={confirmWord}
            onChange={(event) => setConfirmWord(event.target.value)}
            fullWidth
            autoComplete="off"
          />
        )}
      </DialogContent>
      <DialogActions>
        {/* Cancel is focused first, so pressing Enter never publishes. */}
        <Button onClick={onCancel} disabled={publishing} autoFocus>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={highRisk ? 'error' : 'primary'}
          disabled={!canPublish}
          onClick={() => onPublish(note.trim())}
        >
          {publishing ? 'Publishing…' : 'Publish to all users now'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
